import type { HelpTopicPair } from "../types";

const topic: HelpTopicPair = {
  th: {
    id: "oauth-clients",
    title: "OAuth clients (แอปพลิเคชัน)",
    summary:
      "รายชื่อแอปพลิเคชันทั้งหมดที่ใช้ระบบนี้เป็นที่เข้าสู่ระบบ (Single Sign-On) และเป็นที่ลงทะเบียนแอปใหม่ แต่ละแอปเรียกว่า “OAuth client” และมี Client ID ของตัวเอง",
    access:
      "เปิดดูรายการได้เมื่อมีสิทธิ์ clients:read (บทบาท owner, admin และ auditor มีสิทธิ์นี้) ปุ่ม “ลงทะเบียน client” จะแสดงเฉพาะผู้ที่มีสิทธิ์ clients:write (owner และ admin)",
    shot: { file: "oauth-clients-list.png", caption: "หน้ารายการ OAuth clients" },
    sections: [
      {
        id: "what-is-a-client",
        heading: "OAuth client คืออะไร",
        body: [
          "ทุกระบบที่ต้องการให้พนักงานหรือลูกค้า “เข้าสู่ระบบด้วยบัญชีกลาง” (เช่น ระบบ HR, ระบบแชต, พอร์ทัลคู่ค้า) ต้องลงทะเบียนเป็น OAuth client ที่หน้านี้ก่อน เมื่อผู้ใช้กดเข้าสู่ระบบในแอปนั้น แอปจะพาผู้ใช้มาที่หน้าเข้าสู่ระบบกลาง ผู้ใช้กรอกรหัสผ่าน (และรหัสยืนยันสองขั้นตอนถ้ามี) แล้วระบบจะส่งผู้ใช้กลับไปที่แอปพร้อมข้อมูลยืนยันตัวตน",
          "ข้อมูลที่ทีมพัฒนาของแอปต้องใช้มีสามอย่าง: Client ID (เหมือนชื่อผู้ใช้ของแอป), Client secret (เหมือนรหัสผ่านของแอป — เฉพาะประเภท Confidential) และ Redirect URI (ที่อยู่ที่ระบบจะส่งผู้ใช้กลับไปหลังเข้าสู่ระบบ)",
          "คอนโซลผู้ดูแลนี้เองก็เป็น OAuth client ตัวหนึ่ง (ปกติชื่อ platform-console) ซึ่งแพลตฟอร์มจัดการให้ บางส่วนของมันจึงแก้ไขไม่ได้",
        ],
      },
      {
        id: "list",
        heading: "ตารางรายการแอปพลิเคชัน",
        body: [
          "คลิกที่แถวใดก็ได้เพื่อเปิดหน้ารายละเอียดของแอปนั้น (ดูหัวข้อ “รายละเอียด OAuth client”) ปุ่มโหลดใหม่ (ไอคอนลูกศรวน) ดึงข้อมูลล่าสุด แถบเครื่องมือของตารางมีปุ่ม “คอลัมน์” (เลือกคอลัมน์ที่จะแสดง), “ตัวกรอง”, “ความหนาแน่น” และ “ส่งออก” (ดาวน์โหลดตารางเป็น CSV) รวมถึงช่องค้นหา",
          "ถ้ายังไม่มีแอปเลย ตารางจะแสดงข้อความ “ยังไม่มี OAuth client — ลงทะเบียนแอปพลิเคชันแรก”",
        ],
        fieldsTitle: "คอลัมน์ในตาราง",
        fields: [
          {
            name: "ชื่อ",
            description:
              "ชื่อแอปพลิเคชัน ถ้าแอปถูกตั้งเป็นแอปภายใน จะมีป้าย “แอปภายใน” ต่อท้าย คอลัมน์นี้ซ่อนไม่ได้",
          },
          {
            name: "Client ID",
            description:
              "รหัสประจำแอปที่ระบบสร้างให้ ใช้ตั้งค่าในแอป กดไอคอนคัดลอกข้างรหัสเพื่อคัดลอกได้ทันที",
          },
          {
            name: "ประเภท",
            description:
              "Confidential (แอปฝั่งเซิร์ฟเวอร์ที่เก็บ secret ได้) หรือ Public (SPA / มือถือ) — ไม่มี secret",
          },
          {
            name: "แอปภายใน",
            description:
              "แสดงเครื่องหมาย ✓ เมื่อเป็นแอปภายใน (ซ่อนไว้โดยค่าเริ่มต้น เปิดได้จากปุ่ม “คอลัมน์”)",
          },
          {
            name: "ทีมเจ้าของ",
            description: "ทีมที่รับผิดชอบแอป (ซ่อนไว้โดยค่าเริ่มต้น)",
          },
          {
            name: "สิทธิ์เข้าถึง",
            description:
              "ใครเข้าสู่ระบบแอปนี้ได้: ป้ายสีเหลือง “ทุกคน” หมายถึงบัญชีที่ใช้งานอยู่ทุกบัญชี (รวมลูกค้า) หรือรายชื่อประเภทผู้ใช้และกลุ่มที่อนุญาต คั่นด้วย · เช่น employee · support",
          },
          { name: "สร้างเมื่อ", description: "วันที่ลงทะเบียนแอป (ตามเขตเวลาของระบบ)" },
          {
            name: "สถานะ",
            description:
              "เปิดใช้งาน (จุดสีเขียว) หรือ ปิดใช้งาน (จุดสีแดง) — แอปที่ปิดใช้งานจะไม่มีใครเข้าสู่ระบบผ่านแอปนั้นได้",
          },
        ],
        tips: [
          "ใช้ “ตัวกรอง” กับคอลัมน์ “สถานะ” เพื่อหาแอปที่ถูกปิดใช้งาน หรือกับคอลัมน์ “ประเภท” เพื่อแยก Confidential / Public",
          "การตั้งค่าคอลัมน์และความหนาแน่นจะถูกจำไว้ในเบราว์เซอร์ของคุณ",
        ],
      },
      {
        id: "client-types",
        heading: "เลือกประเภท: Confidential หรือ Public",
        body: [
          "Confidential — สำหรับแอปที่ทำงานบนเซิร์ฟเวอร์ (เช่น เว็บที่เขียนด้วย PHP, Java, Python, Go, Next.js ฝั่งเซิร์ฟเวอร์) ซึ่งเก็บความลับไว้ได้โดยผู้ใช้มองไม่เห็น ระบบจะออก Client secret ให้หนึ่งชุด แอปต้องส่ง secret นี้ทุกครั้งที่แลก token",
          "Public (SPA / มือถือ) — สำหรับแอปที่โค้ดทั้งหมดอยู่ในเบราว์เซอร์หรือในเครื่องผู้ใช้ (React/Vue แบบหน้าเดียว, แอปมือถือ) ซึ่งเก็บความลับไม่ได้ จึงไม่มี secret และต้องใช้ PKCE (กลไกป้องกันที่ไลบรารีมาตรฐานทำให้อัตโนมัติ)",
          "ถ้าไม่แน่ใจ ให้ถามทีมพัฒนาของแอปว่า “ขั้นตอนแลก token ทำบนเซิร์ฟเวอร์หรือในเบราว์เซอร์” — ถ้าบนเซิร์ฟเวอร์ให้เลือก Confidential",
        ],
        warnings: [
          "ประเภทของแอปเลือกได้ครั้งเดียวตอนลงทะเบียน เปลี่ยนภายหลังไม่ได้ ถ้าเลือกผิดต้องลงทะเบียนแอปใหม่",
        ],
      },
      {
        id: "register",
        heading: "ลงทะเบียนแอปพลิเคชันใหม่",
        shot: { file: "oauth-clients-register.png", caption: "หน้าต่าง “ลงทะเบียน client”" },
        steps: [
          "กดปุ่ม “ลงทะเบียน client” มุมบนขวาของตาราง",
          "กรอก “ชื่อแอปพลิเคชัน” และเลือก “ประเภท”",
          "กรอก Redirect URI อย่างน้อยหนึ่งรายการ (ขอจากทีมพัฒนาของแอป) กด “เพิ่ม URI” ถ้ามีหลายรายการ",
          "(ไม่บังคับ) กรอก Post-logout redirect URIs, เลือก Scopes, ติ๊ก “แอปภายใน”, กรอก Back-channel logout URI, ทีมเจ้าของ และคำอธิบาย",
          "ในส่วน “สิทธิ์เข้าถึง” ด้านล่าง เลือกว่าใครเข้าสู่ระบบแอปนี้ได้ (ค่าเริ่มต้นคือเฉพาะ employee)",
          "กดปุ่ม “ลงทะเบียน client” ด้านล่างของหน้าต่าง ปุ่มจะกดได้เมื่อมีชื่อ มี Redirect URI อย่างน้อยหนึ่งรายการ และตั้งค่าสิทธิ์เข้าถึงถูกต้อง",
          "ถ้าเป็นประเภท Confidential ระบบจะแสดงหน้าต่าง Client secret ทันที — คัดลอกเก็บไว้ (ดูหัวข้อถัดไป)",
          "ส่ง Client ID, Client secret (ถ้ามี) และ Issuer / Discovery URL ให้ทีมพัฒนา (ดูได้ที่แท็บ “การเชื่อมต่อ” ในหน้ารายละเอียด)",
        ],
        tips: [
          "ถ้าข้อมูลไม่ผ่านการตรวจสอบ ระบบจะแสดงข้อความสีแดงด้านบนของหน้าต่างและใต้ช่องที่ผิด แก้แล้วกดลงทะเบียนอีกครั้งได้",
          "ทุกอย่างยกเว้นประเภทของแอป แก้ไขภายหลังได้ในหน้ารายละเอียด",
        ],
      },
      {
        id: "form-fields",
        heading: "ช่องในฟอร์มลงทะเบียน",
        fieldsTitle: "ช่องในฟอร์ม",
        fields: [
          {
            name: "ชื่อแอปพลิเคชัน",
            required: true,
            description:
              "ชื่อที่ผู้ใช้จะเห็นบนหน้าเข้าสู่ระบบ หน้าขอความยินยอม และหน้า “ไม่มีสิทธิ์เข้าแอปนี้” ยาวไม่เกิน 100 ตัวอักษร เช่น “ระบบ HR”",
          },
          {
            name: "ประเภท",
            required: true,
            description:
              "Confidential (ค่าเริ่มต้น) หรือ Public (SPA / มือถือ) ดูหัวข้อ “เลือกประเภท” — เปลี่ยนภายหลังไม่ได้",
          },
          {
            name: "Redirect URIs",
            required: true,
            description:
              "ที่อยู่ในแอปที่ระบบจะส่งผู้ใช้กลับไปหลังเข้าสู่ระบบสำเร็จ เช่น https://hr.example.com/callback ต้องเป็น https (http ใช้ได้เฉพาะ localhost / 127.0.0.1 สำหรับทดสอบ) หรือ scheme ของแอปมือถือ เช่น com.example.app:/callback ห้ามมีส่วน # ต่อท้าย สูงสุด 20 รายการ ระบบเทียบแบบตรงทุกตัวอักษร — ต่างกันแม้แต่ / ท้ายสุดก็เข้าสู่ระบบไม่ได้",
          },
          {
            name: "Post-logout redirect URIs",
            description:
              "ที่อยู่ที่อนุญาตให้ส่งผู้ใช้กลับไปหลังออกจากระบบ เช่น https://hr.example.com/ กฎรูปแบบเดียวกับ Redirect URI สูงสุด 20 รายการ ถ้าไม่กรอก แอปจะพาผู้ใช้กลับหลังออกจากระบบไม่ได้",
          },
          {
            name: "Scopes ที่อนุญาต",
            description:
              "ชุดข้อมูลของผู้ใช้ที่แอปขอได้ ค่าเริ่มต้นคือ openid, profile, email (openid ติ๊กไว้เสมอและเอาออกไม่ได้) รายละเอียดแต่ละตัวดูหัวข้อ “Scopes แต่ละตัวหมายถึงอะไร” ให้เลือกเฉพาะที่แอปจำเป็นต้องใช้",
          },
          {
            name: "แอปภายใน — แอปภายในจะข้ามหน้าขอความยินยอม",
            description:
              "ติ๊กสำหรับแอปขององค์กรเอง ผู้ใช้จะไม่เห็นหน้า “แอปนี้ขอเข้าถึงข้อมูลของคุณ” ถ้าไม่ติ๊ก (แอปภายนอก) ผู้ใช้จะต้องกดยินยอมหนึ่งครั้งต่อชุด scope",
          },
          {
            name: "Back-channel logout URI",
            description:
              "(สำหรับนักพัฒนา) ที่อยู่ในแอปที่ระบบจะส่งสัญญาณแจ้งเมื่อผู้ใช้ออกจากระบบกลาง เพื่อให้แอปตัดการเชื่อมต่อของผู้ใช้คนนั้นด้วย ต้องเป็น https ไม่บังคับ",
          },
          {
            name: "ทีมเจ้าของ",
            description: "ทีมที่ดูแลแอป ไว้ติดต่อเมื่อมีปัญหา ไม่เกิน 100 ตัวอักษร ไม่บังคับ",
          },
          { name: "คำอธิบาย", description: "บันทึกสั้น ๆ ว่าแอปนี้ใช้ทำอะไร ไม่เกิน 500 ตัวอักษร ไม่บังคับ" },
          {
            name: "สิทธิ์เข้าถึง",
            description:
              "ใครเข้าสู่ระบบแอปนี้ได้ และเปิดให้สมัครสมาชิกเองหรือไม่ แอปใหม่อนุญาตเฉพาะ employee โดยค่าเริ่มต้น เปลี่ยนได้ทันทีหรือภายหลังในแท็บ “สิทธิ์เข้าถึง” (อธิบายละเอียดในหัวข้อ “รายละเอียด OAuth client”)",
          },
        ],
      },
      {
        id: "scopes",
        heading: "Scopes แต่ละตัวหมายถึงอะไร",
        body: [
          "Scope คือ “ชุดข้อมูล” ที่แอปขอเห็นเกี่ยวกับผู้ใช้ แอปขอได้เฉพาะ scope ที่ติ๊กไว้ที่นี่เท่านั้น",
        ],
        fields: [
          { name: "openid", description: "จำเป็นเสมอ ให้รหัสผู้ใช้ถาวร (sub) ที่แอปใช้ระบุตัวผู้ใช้" },
          { name: "profile", description: "ชื่อที่แสดง รูปโปรไฟล์ รหัสผู้ใช้งาน และชื่อผู้ใช้ (ถ้ามี)" },
          { name: "email", description: "อีเมลหลักของผู้ใช้ และสถานะว่ายืนยันแล้วหรือยัง" },
          { name: "phone", description: "เบอร์โทรหลักที่ยืนยันแล้ว" },
          { name: "identities", description: "อีเมลและเบอร์โทรที่ยืนยันแล้วทั้งหมดของบัญชี" },
          {
            name: "offline_access",
            description:
              "ให้แอปได้ refresh token เพื่อให้ผู้ใช้อยู่ในระบบต่อได้นาน (สูงสุด 30 วัน) โดยไม่ต้องเข้าสู่ระบบใหม่",
          },
          { name: "roles", description: "บทบาทของผู้ใช้ในคอนโซลนี้ (owner, admin, auditor …)" },
          {
            name: "groups",
            description:
              "ประเภทผู้ใช้ (employee, customer, …) และกลุ่มที่ผู้ใช้เป็นสมาชิก — ใช้กำหนดสิทธิ์ภายในแอป",
          },
          {
            name: "employee",
            description:
              "ข้อมูลพนักงาน เช่น รหัสพนักงาน แผนก ตำแหน่ง และฟิลด์เพิ่มเติมที่ตั้งให้ส่งไปกับ token",
          },
        ],
      },
      {
        id: "secret",
        heading: "หน้าต่าง Client secret (แสดงครั้งเดียว)",
        shot: {
          file: "oauth-clients-secret.png",
          caption: "หน้าต่าง Client secret หลังลงทะเบียนแอป Confidential",
        },
        body: [
          "หลังลงทะเบียนแอปประเภท Confidential (หรือหลังกด “สร้าง secret ใหม่” ในหน้ารายละเอียด) ระบบจะแสดงหน้าต่าง “Client secret” ที่มี client_id และ client_secret พร้อมปุ่มคัดลอก และคำเตือน “คัดลอก secret นี้ทันที ระบบจะไม่แสดงอีก”",
          "ระบบเก็บ secret ไว้แบบเข้ารหัสทางเดียว จึงไม่มีใครเปิดดู secret เดิมได้อีก แม้แต่ผู้ดูแลระบบ เมื่อปิดหน้าต่างแล้ว ถ้าทำหายต้องสร้าง secret ใหม่เท่านั้น",
        ],
        steps: [
          "กดไอคอนคัดลอกข้าง client_secret",
          "วางลงในที่เก็บความลับที่ปลอดภัย (เช่น password manager หรือระบบเก็บ secret ของทีม) หรือส่งให้ทีมพัฒนาผ่านช่องทางที่ปลอดภัย",
          "กด “ฉันเก็บ secret แล้ว” เพื่อปิดหน้าต่าง",
        ],
        warnings: [
          "ปิดหน้าต่างนี้แล้วจะดู secret อีกไม่ได้ — คัดลอกก่อนปิดเสมอ",
          "อย่าส่ง secret ทางอีเมล แชตกลุ่ม หรือใส่ไว้ในโค้ดที่เผยแพร่ ใครได้ secret ไปสามารถแอบอ้างเป็นแอปนี้ได้",
          "แอปประเภท Public ไม่มี secret จึงไม่มีหน้าต่างนี้",
        ],
      },
    ],
    related: ["client-detail", "groups", "user-types", "audit-logs"],
  },
  en: {
    id: "oauth-clients",
    title: "OAuth clients (applications)",
    summary:
      "The list of every application that uses this platform for sign-in (single sign-on), and the place to register new ones. Each application is called an “OAuth client” and has its own Client ID.",
    access:
      "Viewing the list needs the clients:read permission (the owner, admin and auditor roles have it). The “Register client” button is shown only to people with clients:write (owner and admin).",
    shot: { file: "oauth-clients-list.png", caption: "The OAuth clients list" },
    sections: [
      {
        id: "what-is-a-client",
        heading: "What is an OAuth client?",
        body: [
          "Every system that lets staff or customers “sign in with the central account” (an HR system, a chat app, a partner portal …) must first be registered here as an OAuth client. When a user clicks sign-in in that application, it sends them to the central sign-in page; they enter their password (and two-step code, if any), and the platform sends them back to the application with proof of who they are.",
          "The application's developers need three things: the Client ID (like the application's user name), the Client secret (like its password — Confidential clients only) and the Redirect URI (where the platform sends the user back after sign-in).",
          "This admin console is itself an OAuth client (usually platform-console). It is managed by the platform, so parts of it cannot be changed.",
        ],
      },
      {
        id: "list",
        heading: "The applications table",
        body: [
          "Click any row to open that application's detail page (see “OAuth client details”). The reload button (circular arrow) fetches the latest data. The table toolbar has “Columns” (choose visible columns), “Filters”, “Density” and “Export” (download the table as CSV), plus a search box.",
          "With no applications yet the table shows “No OAuth clients yet — register your first application”.",
        ],
        fieldsTitle: "Columns in the table",
        fields: [
          {
            name: "Name",
            description:
              "The application name. First-party applications show a “First-party” badge next to it. This column cannot be hidden.",
          },
          {
            name: "Client ID",
            description:
              "The application's identifier generated by the platform. Use the copy icon next to it.",
          },
          {
            name: "Type",
            description:
              "Confidential (a server-side app that can keep a secret) or Public (SPA / mobile) — no secret.",
          },
          {
            name: "First-party",
            description: "A ✓ for first-party applications (hidden by default; show it with “Columns”).",
          },
          {
            name: "Owner team",
            description: "The team responsible for the application (hidden by default).",
          },
          {
            name: "Access",
            description:
              "Who may sign in: a yellow “Everyone” badge means every active account (customers included); otherwise the allowed user types and groups separated by ·, e.g. employee · support.",
          },
          {
            name: "Created",
            description: "When the application was registered (in the platform's time zone).",
          },
          {
            name: "Status",
            description:
              "Enabled (green dot) or Disabled (red dot) — nobody can sign in through a disabled application.",
          },
        ],
        tips: [
          "Use “Filters” on “Status” to find disabled applications, or on “Type” to separate Confidential and Public clients.",
          "Column and density choices are remembered in your browser.",
        ],
      },
      {
        id: "client-types",
        heading: "Choosing a type: Confidential or Public",
        body: [
          "Confidential — for applications that run on a server (web apps in PHP, Java, Python, Go, server-side Next.js …) and can keep a secret out of the user's sight. The platform issues one Client secret; the application sends it whenever it exchanges a sign-in for tokens.",
          "Public (SPA / mobile) — for applications whose code runs entirely in the browser or on the user's device (single-page React/Vue apps, mobile apps). They cannot keep a secret, so they get none and must use PKCE (a protection standard libraries handle automatically).",
          "Not sure? Ask the developers whether the token exchange happens on a server or in the browser — on a server means Confidential.",
        ],
        warnings: [
          "The type is chosen once, at registration, and cannot be changed later. If it is wrong, register a new application.",
        ],
      },
      {
        id: "register",
        heading: "Register a new application",
        shot: { file: "oauth-clients-register.png", caption: "The “Register client” dialog" },
        steps: [
          "Click “Register client” at the top right of the table.",
          "Enter the “Application name” and choose the “Type”.",
          "Enter at least one Redirect URI (ask the application's developers). Click “Add URI” for more.",
          "Optionally fill in Post-logout redirect URIs, choose Allowed scopes, tick “First-party”, and enter a Back-channel logout URI, Owner team and Description.",
          "In the “Access” part at the bottom, choose who may sign in (the default is employees only).",
          "Click “Register client” at the bottom of the dialog. It is enabled once there is a name, at least one Redirect URI and a valid access rule.",
          "For a Confidential client the Client secret dialog opens immediately — copy the secret (see below).",
          "Give the developers the Client ID, the Client secret (if any) and the Issuer / Discovery URL (on the “Integration” tab of the detail page).",
        ],
        tips: [
          "If something is invalid, a red message appears at the top of the dialog and under the field; fix it and register again.",
          "Everything except the type can be changed later on the detail page.",
        ],
      },
      {
        id: "form-fields",
        heading: "Fields of the registration form",
        fieldsTitle: "Fields in the form",
        fields: [
          {
            name: "Application name",
            required: true,
            description:
              "Shown to users on the sign-in, consent and “no access” pages. Up to 100 characters, e.g. “HR portal”.",
          },
          {
            name: "Type",
            required: true,
            description:
              "Confidential (default) or Public (SPA / mobile) — see “Choosing a type”. Cannot be changed later.",
          },
          {
            name: "Redirect URIs",
            required: true,
            description:
              "Where the platform sends users back after a successful sign-in, e.g. https://hr.example.com/callback. Must be https (http only for localhost / 127.0.0.1 while testing) or a mobile app scheme such as com.example.app:/callback; no # part; up to 20. Matched character by character — even a missing trailing / makes sign-in fail.",
          },
          {
            name: "Post-logout redirect URIs",
            description:
              "Where users may be sent back after signing out, e.g. https://hr.example.com/. Same rules as Redirect URIs, up to 20. Without one the application cannot bring users back after sign-out.",
          },
          {
            name: "Allowed scopes",
            description:
              "The sets of user data the application may request. Default: openid, profile, email (openid is always on and cannot be removed). See “What each scope means”; allow only what the application needs.",
          },
          {
            name: "First-party — First-party apps skip the consent screen",
            description:
              "Tick for the organization's own applications: users do not see the “this app wants to access your data” screen. Third-party applications ask for consent once per set of scopes.",
          },
          {
            name: "Back-channel logout URI",
            description:
              "(For developers) An address in the application that the platform notifies when a user signs out centrally, so the application can end that user's session too. https; optional.",
          },
          {
            name: "Owner team",
            description: "The team that looks after the application. Up to 100 characters; optional.",
          },
          {
            name: "Description",
            description: "A short note on what the application is for. Up to 500 characters; optional.",
          },
          {
            name: "Access",
            description:
              "Who may sign in, and whether visitors may sign up. New applications allow employees only; change it now or later on the “Access” tab (explained in “OAuth client details”).",
          },
        ],
      },
      {
        id: "scopes",
        heading: "What each scope means",
        body: [
          "A scope is a set of information about the user that the application asks for. Applications can only request the scopes ticked here.",
        ],
        fields: [
          {
            name: "openid",
            description: "Always required: the permanent user ID (sub) the application identifies users by.",
          },
          { name: "profile", description: "Display name, picture, the user code and the username (if set)." },
          { name: "email", description: "The primary email address and whether it is verified." },
          { name: "phone", description: "The primary verified phone number." },
          {
            name: "identities",
            description: "All verified email addresses and phone numbers of the account.",
          },
          {
            name: "offline_access",
            description:
              "Gives the application a refresh token so users stay signed in for longer (up to 30 days) without signing in again.",
          },
          { name: "roles", description: "The user's roles in this console (owner, admin, auditor …)." },
          {
            name: "groups",
            description:
              "The user type (employee, customer, …) and the groups the user belongs to — for permissions inside the app.",
          },
          {
            name: "employee",
            description:
              "Employee data such as employee code, department, position and custom fields marked to be sent with tokens.",
          },
        ],
      },
      {
        id: "secret",
        heading: "The Client secret dialog (shown once)",
        shot: {
          file: "oauth-clients-secret.png",
          caption: "The Client secret dialog after registering a Confidential client",
        },
        body: [
          "After registering a Confidential application (or after “Rotate secret” on the detail page) the “Client secret” dialog shows client_id and client_secret with copy buttons, and the warning “Copy this secret now. It will not be shown again.”",
          "The platform stores the secret one-way encrypted, so nobody — not even administrators — can look it up again after the dialog is closed. A lost secret can only be replaced with a new one.",
        ],
        steps: [
          "Click the copy icon next to client_secret.",
          "Paste it into a secure secret store (a password manager or your team's secret vault), or hand it to the developers through a secure channel.",
          "Click “I have stored the secret” to close the dialog.",
        ],
        warnings: [
          "Once the dialog is closed the secret cannot be shown again — always copy it first.",
          "Never send the secret by email or group chat or put it in published code: anyone holding it can impersonate the application.",
          "Public applications have no secret, so this dialog does not appear for them.",
        ],
      },
    ],
    related: ["client-detail", "groups", "user-types", "audit-logs"],
  },
};

export default topic;
